/**
 * Hand-illustrated, editorial line + soft-fill icon set for Just One Drink.
 * `currentColor` drives the stroke; two-tone accent fills are passed via `fill`.
 * Ported from the design handoff (icons.jsx) into typed React components.
 */
import type { CSSProperties, ReactNode } from "react";

export interface IconProps {
  /** Pixel size (width = height). */
  size?: number;
  /** Accent fill for two-tone glyphs. */
  fill?: string;
  className?: string;
  style?: CSSProperties;
}

interface IcProps {
  size?: number;
  sw?: number;
  vb?: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

function Ic({ size = 24, sw = 1.6, vb = 24, className, style, children }: IcProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${vb} ${vb}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/* ---------- drinks (two-tone) ---------- */
export const IconBeer = ({ size, fill = "oklch(78% 0.13 72 / 35%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M6 8h8v10a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3V8Z" fill={fill} />
    <path d="M14 10h2.5a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5H14" />
    <path
      d="M6.4 8c-.3-2.2 1-3.4 2.6-3.2.5-1.6 3-1.8 3.7-.2 1.8-.5 3 .8 2.5 2.4"
      fill="oklch(95% 0.02 80 / 18%)"
    />
    <path d="M9 12v5M11.4 12v5" stroke="oklch(78% 0.13 72 / 70%)" />
  </Ic>
);
export const IconCocktail = ({ size, fill = "oklch(86% 0.18 120 / 32%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M4.5 6h15l-7.5 8-7.5-8Z" fill={fill} />
    <path d="M12 14v5M8.5 19h7" />
    <circle cx="15.5" cy="6.4" r="1.1" fill="var(--rose)" stroke="none" />
    <path d="M15.5 6.4 18 3.5" />
  </Ic>
);
export const IconShot = ({ size, fill = "oklch(78% 0.13 72 / 40%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M8 5h8l-1 13a2 2 0 0 1-2 1.8h-2A2 2 0 0 1 9 18L8 5Z" fill={fill} />
    <path d="M8.4 10h7.2" />
  </Ic>
);
export const IconBottle = ({ size, fill = "oklch(70% 0.12 122 / 30%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path
      d="M10 3h4v3l1.2 2.4c.5 1 .8 2 .8 3.1V19a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-7.5c0-1.1.3-2.1.8-3.1L10 6V3Z"
      fill={fill}
    />
    <path d="M8.8 13h6.4" />
    <rect x="9.4" y="14.4" width="5.2" height="4.4" rx="1" fill="oklch(95% 0.02 80 / 12%)" />
  </Ic>
);
export const IconMixer = ({ size, fill = "oklch(95% 0.02 80 / 16%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="7.5" y="3.5" width="9" height="17" rx="3" fill={fill} />
    <path d="M7.6 9h8.8" />
    <path d="M10 3.5V2.4M14 3.5V2.4" />
    <path d="M10.5 13v3M13.5 12v4" stroke="oklch(86% 0.18 120 / 60%)" />
  </Ic>
);
export const IconSnack = ({ size, fill = "oklch(78% 0.13 72 / 30%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path
      d="M4 13c0-1 .6-1.5 1.5-1.5h13c.9 0 1.5.5 1.5 1.5 0 3.6-3.6 6.5-8 6.5s-8-2.9-8-6.5Z"
      fill={fill}
    />
    <path d="M3 19.5h18" />
    <path d="M8 11c0-2 1.6-3.5 4-3.5s4 1.5 4 3.5" />
    <circle cx="10" cy="9.6" r=".5" fill="currentColor" />
    <circle cx="13.4" cy="9.3" r=".5" fill="currentColor" />
  </Ic>
);
export const IconCoffee = ({ size, fill = "oklch(60% 0.08 60 / 30%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M5 8h11v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V8Z" fill={fill} />
    <path d="M16 9.5h2a2 2 0 0 1 0 4h-2" />
    <path
      d="M8 3.5c-.6.8-.6 1.6 0 2.4M11.5 3.5c-.6.8-.6 1.6 0 2.4"
      stroke="oklch(95% 0.02 80 / 45%)"
    />
  </Ic>
);

/* ---------- BRAND MARK — single coupe + one bubble ("one drink") ---------- */
export const IconMark = ({ size, fill = "oklch(78% 0.13 72 / 34%)", ...p }: IconProps) => (
  <Ic size={size} sw={1.5} {...p}>
    <path d="M5.4 6.2 H18.6" />
    <path d="M5.4 6.2 C7 11.4, 17 11.4, 18.6 6.2 Z" fill={fill} />
    <path d="M12 11.4 V18.4" />
    <path d="M8.6 18.6 H15.4" />
    <circle cx="12" cy="3.4" r="1.5" fill="var(--gold-500)" stroke="none" />
    <path d="M12 5 V5.9" stroke="var(--gold-500)" />
  </Ic>
);

/* ---------- cheers (clinking glasses) action icon ---------- */
export const IconCheers = ({ size, fill = "oklch(78% 0.13 72 / 32%)", ...p }: IconProps) => (
  <Ic size={size} sw={1.5} {...p}>
    <path d="M9.5 4 7 11.5c-.5 1.6.4 3 2 3.3l-.6 4.7M5.7 20.5h5.2" fill="none" />
    <path d="M9.5 4 7 11.5h4.3L9.5 4Z" fill={fill} />
    <path d="M14.5 4 17 11.5c.5 1.6-.4 3-2 3.3l.6 4.7M13 20.5h5.2" fill="none" />
    <path d="M14.5 4 17 11.5h-4.3L14.5 4Z" fill={fill} />
    <path d="M11.5 3 13 5M12.8 2.2 13.8 3.4" stroke="var(--gold-500)" />
  </Ic>
);

/* gift drink card mark */
export const IconGiftDrink = ({ size, fill = "oklch(78% 0.13 72 / 30%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="3.5" y="7" width="17" height="13" rx="3" fill={fill} />
    <path d="M3.5 11h17" />
    <path d="M12 7v13" stroke="oklch(95% 0.02 80 / 40%)" />
    <path
      d="M12 7c-1.5-3.5-5-3-5-.6 0 .4.3.6.7.6H12Zm0 0c1.5-3.5 5-3 5-.6 0 .4-.3.6-.7.6H12Z"
      fill="oklch(72% 0.11 30 / 40%)"
    />
  </Ic>
);

/* ---------- venue / social ---------- */
export const IconTable = ({ size, fill = "oklch(95% 0.02 80 / 12%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <ellipse cx="12" cy="11" rx="8" ry="3.4" fill={fill} />
    <path d="M6 12.6V19M18 12.6V19M9.5 13.4V20M14.5 13.4V20" />
  </Ic>
);
export const IconPeople = ({ size, fill = "oklch(95% 0.02 80 / 14%)", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <circle cx="9" cy="8" r="3" fill={fill} />
    <circle cx="16.5" cy="9" r="2.4" fill={fill} />
    <path d="M3.5 19c.4-3 2.6-4.6 5.5-4.6S14 16 14.5 19" />
    <path d="M15 14.6c2.4.1 4.2 1.6 4.6 4.4" />
  </Ic>
);

/* ---------- bottom-nav glyphs ---------- */
export const IconHome = ({ size, fill = "none", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M4 11 12 4l8 7" fill={fill} />
    <path d="M6 10v9h12v-9" fill={fill} />
    <path d="M10 19v-4h4v4" />
  </Ic>
);
export const IconMenuGlyph = ({ size, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="4.5" y="4.5" width="15" height="15" rx="3" />
    <path d="M8 9h8M8 12h8M8 15h5" />
  </Ic>
);
export const IconAround = ({ size, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <circle cx="12" cy="12" r="8.2" />
    <path d="M14.5 9.5 11 11l-1.5 3.5L13 13l1.5-3.5Z" fill="var(--gold-500)" stroke="none" />
    <circle cx="12" cy="12" r=".9" fill="currentColor" />
  </Ic>
);
export const IconGiftNav = ({ size, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="4" y="9" width="16" height="11" rx="2.5" />
    <path d="M4 13h16M12 9v11" />
    <path d="M12 9c-1.3-3-4.4-2.6-4.4-.5 0 .3.2.5.6.5H12Zm0 0c1.3-3 4.4-2.6 4.4-.5 0 .3-.2.5-.6.5H12Z" />
  </Ic>
);
export const IconMe = ({ size, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <circle cx="12" cy="8.5" r="3.6" />
    <path d="M5 20c.6-3.7 3-5.6 7-5.6s6.4 1.9 7 5.6" />
  </Ic>
);

/* ---------- ui glyphs ---------- */
export const IconClose = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} sw={1.8} {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Ic>
);
export const IconChevDown = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} sw={1.8} {...p}>
    <path d="M6 9l6 6 6-6" />
  </Ic>
);
export const IconBack = ({ size = 20, ...p }: IconProps) => (
  <Ic size={size} sw={1.8} {...p}>
    <path d="M15 5l-7 7 7 7" />
  </Ic>
);
export const IconCheck = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} sw={2} {...p}>
    <path d="M5 12.5l4.5 4.5L19 6.5" />
  </Ic>
);
export const IconPlus = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} sw={2} {...p}>
    <path d="M12 5v14M5 12h14" />
  </Ic>
);
export const IconMinus = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} sw={2} {...p}>
    <path d="M5 12h14" />
  </Ic>
);
export const IconLock = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="5" y="10" width="14" height="9" rx="2.5" />
    <path d="M8 10V8a4 4 0 0 1 8 0v2" />
  </Ic>
);
export const IconEye = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.6" />
  </Ic>
);
export const IconHeart = ({ size = 18, fill = "none", ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path
      d="M12 20s-7-4.3-7-9.4A3.8 3.8 0 0 1 12 7a3.8 3.8 0 0 1 7 3.6C19 15.7 12 20 12 20Z"
      fill={fill}
    />
  </Ic>
);
export const IconIG = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="4" y="4" width="16" height="16" rx="5" />
    <circle cx="12" cy="12" r="3.6" />
    <circle cx="16.6" cy="7.4" r=".9" fill="currentColor" stroke="none" />
  </Ic>
);
export const IconChat = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M4.5 6.5A2.5 2.5 0 0 1 7 4h10a2.5 2.5 0 0 1 2.5 2.5v6A2.5 2.5 0 0 1 17 15H9l-4 3.5V6.5Z" />
  </Ic>
);
export const IconShield = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M12 3 5 6v5c0 4.4 3 7.5 7 9 4-1.5 7-4.6 7-9V6l-7-3Z" />
    <path d="M9.5 12l1.8 1.8L15 9.8" />
  </Ic>
);
export const IconSparkle = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} sw={1.5} {...p}>
    <path
      d="M12 4c.4 3.4 1.6 4.6 5 5-3.4.4-4.6 1.6-5 5-.4-3.4-1.6-4.6-5-5 3.4-.4 4.6-1.6 5-5Z"
      fill="var(--gold-500)"
      stroke="none"
    />
    <path
      d="M18.5 13c.2 1.4.7 1.9 2 2-1.3.2-1.8.7-2 2-.2-1.3-.7-1.8-2-2 1.3-.1 1.8-.6 2-2Z"
      fill="var(--gold-500)"
      stroke="none"
    />
  </Ic>
);
export const IconClock = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <circle cx="12" cy="12" r="8" />
    <path d="M12 8v4.2l2.6 1.6" />
  </Ic>
);
export const IconBell = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16Z" />
    <path d="M10 19.5a2 2 0 0 0 4 0" />
  </Ic>
);
export const IconSearch = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <circle cx="11" cy="11" r="6" />
    <path d="M16 16l4 4" />
  </Ic>
);
export const IconGame = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="3" y="7" width="18" height="10" rx="4" />
    <path d="M7.5 11v2.4M6.3 12.2h2.4" />
    <circle cx="15.5" cy="11.4" r=".9" fill="currentColor" stroke="none" />
    <circle cx="17.4" cy="13.4" r=".9" fill="currentColor" stroke="none" />
  </Ic>
);
export const IconQR = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <rect x="4" y="4" width="6" height="6" rx="1.5" />
    <rect x="14" y="4" width="6" height="6" rx="1.5" />
    <rect x="4" y="14" width="6" height="6" rx="1.5" />
    <path d="M14 14h2v2M20 14v2M16 18v2h4v-2M18 16h.01" />
  </Ic>
);
export const IconWifiOff = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M3 7c5-4 13-4 18 0M6 11c3.4-2.6 8.6-2.6 12 0M9 15c1.8-1.3 4.2-1.3 6 0" />
    <circle cx="12" cy="18.5" r=".6" fill="currentColor" />
    <path d="M3 3l18 18" stroke="var(--destructive)" />
  </Ic>
);
export const IconMoon = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path
      d="M19 13.5A7.5 7.5 0 0 1 10.5 5 7.5 7.5 0 1 0 19 13.5Z"
      fill="oklch(78% 0.13 72 / 25%)"
    />
  </Ic>
);
export const IconNote = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M6 3.5h8L18.5 8v12.5h-13V3.5Z" />
    <path d="M13.5 3.5V8h5M8 12h8M8 15.5h5" />
  </Ic>
);
export const IconRepeat = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M4 9a5 5 0 0 1 5-5h7l-2-2M20 15a5 5 0 0 1-5 5H8l2 2" />
  </Ic>
);
export const IconFlag = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M6 21V4M6 4.5h11l-2.2 3.5L17 11.5H6" />
  </Ic>
);
export const IconMusic = ({ size = 18, ...p }: IconProps) => (
  <Ic size={size} {...p}>
    <path d="M9 18V6l10-2v12" />
    <circle cx="6.5" cy="18" r="2.5" fill="oklch(86% 0.18 120 / 30%)" />
    <circle cx="16.5" cy="16" r="2.5" fill="oklch(86% 0.18 120 / 30%)" />
  </Ic>
);

/** Resolve a drink icon by its menu-data name (e.g. "IconBeer"). */
const DRINK_ICONS: Record<string, (p: IconProps) => ReactNode> = {
  IconBeer,
  IconCocktail,
  IconShot,
  IconBottle,
  IconMixer,
  IconSnack,
  IconCoffee,
};

export function DrinkIcon({ name, ...props }: IconProps & { name: string }) {
  const Comp = DRINK_ICONS[name] ?? IconCocktail;
  return <Comp {...props} />;
}
